"use client";

import type { ScrollShadowProps } from "@heroui/react";

import React from "react";
import { ScrollShadow } from "@heroui/react";
import { cn } from "@heroui/react";
import { getProjectsForSection } from "@/data/projects";
import { SectionLayout } from "@/components/layout/SectionLayout";
import { motion } from "framer-motion";

interface ScrollingBannerProps extends ScrollShadowProps {
  isReverse?: boolean;
  showShadow?: boolean;
  shouldPauseOnHover?: boolean;
  isVertical?: boolean;
  gap?: string;
  duration?: number; // in seconds
}

export const ScrollingBannerSection = React.forwardRef<
  HTMLDivElement,
  ScrollingBannerProps
>(
  (
    {
      className,
      isReverse,
      isVertical = false,
      gap = "2.5rem",
      showShadow = true,
      shouldPauseOnHover = true,
      duration = 20,
      style,
      ...props
    },
    ref,
  ) => {
    const shadowProps: ScrollShadowProps = {
      isEnabled: showShadow,
      offset: -20,
      size: 300,
      orientation: isVertical ? "vertical" : "horizontal",
      visibility: "both",
      ...props,
    };

    // Filtrer les projets qui doivent être affichés dans la scrolling banner
    const projectsToShow = getProjectsForSection("scrollingBanner");

    return (
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
          ease: "easeOut",
          delay: 1.2, // Apparaît après la hero section
        }}
        className="px-4 py-60 ph:px-24 md:px-48 lg:px-72 xl:px-96"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.6,
            delay: 1.4, // Légèrement après le container
          }}
        >
          <ScrollShadow
            {...shadowProps}
            ref={ref}
            className={cn(
              "flex",
              {
                "w-full": !isVertical,
                "overflow-y-hidden": isVertical,
                "overflow-x-hidden": !isVertical,
                "max-h-[calc(100vh_-_200px)]": isVertical,
              },
              className,
            )}
            style={{
              // @ts-ignore
              "--gap": gap,
              "--duration": `${duration}s`,
              ...style,
            }}
          >
            <div
              className={cn("flex w-max items-stretch gap-[--gap]", {
                "flex-col": isVertical,
                "h-full": isVertical,
                "animate-scrolling-banner": !isVertical,
                "animate-scrolling-banner-vertical": isVertical,
                "[animation-direction:reverse]": isReverse,
                "hover:[animation-play-state:paused]": shouldPauseOnHover,
              })}
            >
              {projectsToShow.map(
                (project) =>
                  project.bannerImage && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      key={project.id}
                      src={project.bannerImage}
                      alt={`Logo ${project.name} - Client Virtuosa, agence web premium`}
                      className="h-12 w-auto"
                    />
                  ),
              )}
            </div>
          </ScrollShadow>
        </motion.div>
      </motion.section>
    );
  },
);

ScrollingBannerSection.displayName = "ScrollingBannerSection";
